import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentMultiSuitabilityData,
  type EnrollmentMultiSuitabilitySubmitInput,
  type EnrollmentMultiSuitabilitySubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Multi Suitability data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiSuitabilityData>
 * @example const response = await getEnrollmentMultiSuitabilityData()
 */
export const getEnrollmentMultiSuitabilityData = async (
  context?: DefaultContext,
): Promise<EnrollmentMultiSuitabilityData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiSuitabilityData',
      query: `mutation getEnrollmentMultiSuitabilityData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              progress
              isBrokerDealer
              isDirectorPolicyMakerOfCompany
              directorPolicyMakerOfCompany
              investmentProfile {
                isPoliticallyExposedPerson
                affiliatedPoliticalOrganizations
                hasPoliticallyExposedImmediateFamilyMembers
                politicallyExposedImmediateFamilyMembers
              }
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiSuitabilityData }
  return response.submitEnrollment
}

export const useGetEnrollmentMultiSuitabilityData = createQueryHook<undefined, EnrollmentMultiSuitabilityData>(
  getEnrollmentMultiSuitabilityData,
)

/**
 * Submit Enrollment Multi Suitability data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiSuitabilitySubmitOutput>
 * @example const response = await submitEnrollmentMultiSuitabilityData();
 */
export const submitEnrollmentMultiSuitabilityData = async (
  enrollmentSuitabilitySubmitInput: EnrollmentMultiSuitabilitySubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiSuitabilitySubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiSuitabilityData',
      query: `mutation submitEnrollmentMultiSuitabilityData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentSuitabilitySubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiSuitabilitySubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiSuitabilityData = createMutationHook<
  EnrollmentMultiSuitabilitySubmitInput,
  EnrollmentMultiSuitabilitySubmitOutput
>(submitEnrollmentMultiSuitabilityData)
